import 'regenerator-runtime/runtime';
import { NOT_FOUND, redirect } from 'redux-first-router';
import { fetchData } from './utils/async';

// Customer specific data is loaded in frontend.
// Generic data for all clients is SSR
const clientUpdate = (dispatch, getState) => {
	if (typeof window !== 'undefined') {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'PageView',
			values: {
				path: getState().location.pathname,
			},
		});
	}
};

const clientInit = async (dispatch, getState) => {
	clientUpdate(dispatch, getState);

	dispatch({ type: 'SITE_UPDATED' });

	return getState();
};

const languagePrefix = ''; // '/:language?';
const useLanguage = (str = '') => `${languagePrefix}${str}`;

export default {
	CLIENT_INIT: {
		thunk: async (dispatch, getState) => clientInit(dispatch, getState).catch((err) => console.log('DEBUG: error initiating client site data', err)),
	},
	STATIC: {
		path: '/static/:noop?',
	},
	START: {
		path: useLanguage('/'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) : path;

			// best guess that it's start page otherwise cached...
			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const startPage = !pageData || !pageData.start ? fetchData('', body) : pageData.start;
			const { hash = '' } = bag.action.payload;
			let startPageData;
			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'start' });
		},
	},
	INDEXES: {
		path: useLanguage('/index'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { hash = '' } = bag.action.payload;
			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			return dispatch({ type: 'PAGE_LOADED', paths: 'index', pageType: 'index' });
		},
	},
	EDITORIAL: {
		path: useLanguage('/editorial/:path+'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			let path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				path = path.slice(0, path.indexOf('?'));
			}

			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const page = pageData[`editorial/${path}`] || fetchData(`editorial/${path}`, body);
			const { hash = '' } = bag.action.payload;
			let pageDataReturn = {};
			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			try {
				pageDataReturn = await page;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: `editorial/${path}` });
			return dispatch({ type: 'PAGE_LOADED', paths: pageDataReturn.paths || `editorial/${path}`, pageType: pageDataReturn.pageType || 'page' });
		},
	},
	CATEGORY_PRODUCT_PAGE: {
		path: useLanguage('/:path+'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			/*
				USE CASES:
				/women/clothing/dresses/harper - Product
				/women/clothing/harper - Product
				/women/clothing - Category
				/en/women/clothing - Category with localization
				/sv/dam/kläder - Category with right localization but wrong locale - redirect to english version.
				/en/page
				/page
			*/
			const { location, environment, pageData = {} } = getState();
			const { setup } = environment;
			const { hash = '', fakeUpdate = false } = bag.action.payload;
			let language = languagePrefix !== '' ? location.payload.language : false;
			// Clean any encoded URI characters
			let path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			let search = '';

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			// "language" is probably not a language but a path.
			if (!language && language.length > 2) {
				path = `${language}/${path}`;
				language = null;
			}

			if (fakeUpdate) return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: 'category' });

			// Get content for site.
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			const page = pageData[path] || fetchData(path, body);
			let pageDataReturn = {};
			let listingDataReturn;
			clientUpdate(dispatch, getState);

			try {
				pageDataReturn = await page;
			} catch (err) {
				console.log(`DEBUG: failed page data not retrieved. ${path}`, err);
			}

			if (pageDataReturn && pageDataReturn.is404) {
				dispatch({ type: NOT_FOUND });
				return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: '404' });
			}

			if (pageDataReturn.paths) {
				if (language !== false && path !== pageDataReturn.paths[language] && !pageDataReturn.is404 && pageDataReturn.paths[language]) {
					return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language, path: pageDataReturn.paths[language], search, hash } }));
				}
			}

			dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: path });

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash: currentHash });

			if (listingDataReturn) dispatch({ type: 'LISTING', data: listingDataReturn, listing: listingDataReturn.paths.replace('listing/', '') });
			return dispatch({ type: 'PAGE_LOADED', paths: pageDataReturn.paths || path, pageType: pageDataReturn.pageType || 'page' });
		},
	},
};
